import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getApiBase } from '../utils/apiBase';
import { memorialManageHref } from '../utils/memorial';
import { useAuth } from '../contexts/AuthContext';
import { MemorialPublicExperience } from '../components/memorial/MemorialPublicExperience';

const API = getApiBase();

export default function MemorialPublicPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [manageUrl, setManageUrl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/public/memorial/${slug}`);
      setData(res.data);
      setError(null);
    } catch {
      setData(null);
      setError('This memorial is private or not available.');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!user || !slug) {
      setManageUrl(null);
      return undefined;
    }
    let cancelled = false;
    axios
      .get(`${API}/memorials-api`)
      .then(({ data: payload }) => {
        if (cancelled) return;
        const item = (payload.items || []).find((entry) => entry.memorial?.slug === slug);
        setManageUrl(item?.person ? memorialManageHref(item.person) : null);
      })
      .catch(() => {
        if (!cancelled) setManageUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user, slug]);

  if (loading) {
    return (
      <div className="memorial-public flex min-h-screen items-center justify-center bg-[hsl(152_28%_14%)]">
        <Loader2 className="h-8 w-8 animate-spin text-[#c9a961]" aria-label="Loading memorial page" />
      </div>
    );
  }

  if (error || !data?.person) {
    return (
      <div className="memorial-public flex min-h-screen flex-col items-center justify-center gap-4 bg-[hsl(42_28%_96%)] px-6 text-center">
        <Lock className="h-10 w-10 text-muted-foreground" aria-hidden />
        <p className="font-serif text-xl text-[hsl(24_14%_18%)]">{error || 'Not found'}</p>
        <p className="max-w-sm text-sm text-muted-foreground">
          This memorial may be private, still in draft, or no longer available.
        </p>
        <Link to="/" className="text-sm text-[#8b7355] underline-offset-4 hover:underline">Return home</Link>
      </div>
    );
  }

  return (
    <MemorialPublicExperience
      slug={slug}
      person={data.person}
      memorial={data.memorial}
      sections={data.sections}
      biography={data.biography}
      writtenMemories={data.written_memories}
      timeline={data.timeline}
      photoGallery={data.photo_gallery}
      memories={data.memories}
      profileVideo={data.profile_video}
      videoLinks={data.video_links}
      music={data.music}
      soundtrack={data.soundtrack}
      atmosphere={data.atmosphere}
      treasuredThings={data.treasured_things}
      tributes={data.tributes}
      qr={data.qr}
      reactions={data.reactions || {}}
      support={data.support}
      allowTributes={data.allow_tributes !== false}
      requireEmailForTributes={Boolean(data.require_email_for_tributes)}
      enableReactions={data.enable_reactions !== false}
      showReactionCounts={data.show_reaction_counts !== false}
      ownerPreview={Boolean(manageUrl)}
      manageUrl={manageUrl}
      onTributeSubmitted={load}
    />
  );
}
